// livestreamRatingHintActions.js
'use strict';
import { MERGE_OPERATION_DATA, REMOVE_OPERATION_DATA } from '../ActionTypes.js';
import { RATING_LIVESTREAM_VIEWER_OPEN_VANISH_SECONDS } from '../RemoteConfigKeys.js';

import submitLivestreamRating from '../action/submitLivestreamRating.js';
import toggleModal from '../action/toggleModal.js';

import getOperationData from '../selector/getOperationData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';

import { ModalId } from '../resource/trackingConstants.js';

import { breakpoint } from '../style/variables.js';

/**
 * Open livestream rating hint and also start auto close timer
 * @kind action
 * @param {object} options - The options object.
 * @param {string} options.streamId - stream id.
 * @param {string} options.sessionId - session id.
 * @return {Promise} Action promise.
 */
export const openLivestreamRatingHint =
  ({ streamId } = {}) =>
  async (dispatch, getState) => {
    const deviceWidth = getOperationData(getState(), ['device'], 'width');
    const isOnDesktop = deviceWidth > breakpoint.tablet;

    dispatch(
      toggleModal({
        modalId: 'LivestreamRatingHint',
        payload: {
          streamId,
          trackingModalId: isOnDesktop
            ? ModalId.All.RatingModal
            : ModalId.All.ModalRatingButton,
        },
        modalOption: {
          isLowerThanAll: true,
        },
        isOpen: true,
      })
    );
    dispatch(startLivestreamRatingAutoCloseTimer({ streamId }));
  };

/**
 * Start livestream rating auto close timer
 * @kind action
 * @param {object} options - The options object.
 * @param {string} options.streamId - stream id.
 * @return {Promise} Action promise.
 */
const startLivestreamRatingAutoCloseTimer =
  ({ streamId } = {}) =>
  async (dispatch, getState) => {
    const closeSeconds = getRemoteConfigData(
      getState(),
      RATING_LIVESTREAM_VIEWER_OPEN_VANISH_SECONDS
    );

    const timerId = setTimeout(() => {
      dispatch(submitEmptyStreamRatingThenClose({ streamId }));
    }, closeSeconds * 1000);

    dispatch({
      type: MERGE_OPERATION_DATA,
      payload: {
        selectPath: ['timer', 'livestream', streamId, 'closeRating'],
        data: {
          timerId,
          unixTimestamp: Date.now() + closeSeconds * 1000,
        },
      },
    });
  };

/**
 * Submit empty livestream rating and close rating
 * @kind action
 * @param {object} options - The options object.
 * @param {string} options.streamId - stream id.
 * @return {Promise} Action promise.
 */
export const submitEmptyStreamRatingThenClose =
  ({ streamId } = {}) =>
  async dispatch => {
    dispatch(submitLivestreamRating({ streamId, rating: null }));
    ['LivestreamRatingHint', 'LivestreamRatingModal'].forEach(modalId =>
      dispatch(toggleModal({ modalId, isOpen: false }))
    );
    dispatch(cancelLivestreamRatingAutoCloseTimer({ streamId }));
  };

/**
 * Cancel livestream rating auto close timer
 * @kind action
 * @param {object} options - The options object.
 * @param {string} options.streamId - stream id.
 * @return {Promise} Action promise.
 */
export const cancelLivestreamRatingAutoCloseTimer =
  ({ streamId } = {}) =>
  async (dispatch, getState) => {
    const timerId = getOperationData(
      getState(),
      ['timer', 'livestream', streamId, 'closeRating'],
      'timerId'
    );
    if (!timerId) return dispatch({ type: '' });
    clearTimeout(timerId);
    dispatch({
      type: REMOVE_OPERATION_DATA,
      payload: {
        selectPath: ['timer', 'livestream', streamId, 'closeRating'],
      },
    });
  };
